import { useEffect, useState } from 'react';
import { uiLang } from './i18n.js';
import { call, getToken } from './api.js';

/* ══════════════════════════════════════════════════════════════════════════
 * WHERE THE ELEATER IS — ASKED ON OPEN, NOT HUNTED FOR
 *
 * Al, 24 September 2026: *"right now its still showing location. not my
 * current location. so thats a no no. you have to get that working where ever
 * i am on this planet."*  And: *"if Guru is looking at it, it should show him
 * in Calgary. If Yuan is seeing it here in Shenzhen, it should show her in
 * Futian."*
 *
 * ⚠️ FINDING SOMEBODY WAS ALREADY BUILT — IT JUST NEVER RAN BY ITSELF.
 * `Location.jsx` has done this correctly since it was written, but only when
 * an Eleater went to that screen and pressed a button. Nobody does. So Home
 * read "Set location" for every person who had never been there, which is
 * everybody. The code was right and the moment was wrong.
 *
 * ── WHY IT IS NOT MANDATORY, THOUGH AL ASKED FOR MANDATORY ──────────────────
 *
 * He asked for a hard requirement — *"we must find them from the location.
 * That's it. It's a hard sell."* — and was shown why that is a trap, and
 * agreed to this instead.
 *
 * ⚠️ A REFUSAL IS PERMANENT AND CANNOT BE RE-ASKED. Once a browser records
 * "denied" for a site, there is no API that prompts again. The person would
 * have to go into iPhone Settings → Safari → Location and find Eleat. So a
 * hard requirement turns one mis-tap — a reflex, a borrowed phone — into an
 * app that is dead for that person for ever, and Eleat would never know it
 * happened. web.dev states it plainly: recovering from the blocked state is
 * deliberately made difficult; design the capability as optional.
 *
 * ⚠️ AND THE BIGGEST NAME IN THE BUSINESS AGREES. Uber — the app most
 * dependent on position on earth — documents that choosing "Never" or "Deny"
 * still leaves the app usable: *"you can still use the app by entering pickup
 * and dropoff locations manually."*  OpenTable's own privacy policy describes
 * two paths: generic city-level location, OR precise location with consent.
 * Nobody hard-requires it. Eleat does what they do.
 *
 * ── WHAT ACCURACY ACTUALLY IS, SO NOBODY PROMISES FIVE METRES ───────────────
 *
 * ⚠️ AL ASKED FOR "within five meters" AND HONG KONG CANNOT GIVE IT. Field
 * research from PolyU in Hong Kong's own urban canyons measures smartphone
 * GNSS error at roughly 6.7–13 m, with 92.5% of it coming from signals
 * bouncing off towers rather than arriving directly. Five metres is close to
 * the best case in open sky.
 *
 * That is fine for what Eleat does and the schema already knows why: Central
 * and Sheung Wan are 400 m apart, comfortably outside that error, so the AREA
 * is reliable. What is not reliable is a distance quoted to the metre. Nothing
 * here claims one.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ THE SAME KEY `Location.jsx` HAS ALWAYS USED. Changing it would orphan the
 * choice of everybody who has already set one, which is a data loss nobody
 * asked for dressed up as a refactor. */
const KEY = 'eleat.centre';

/* ⚠️ WHETHER THE SHEET HAS BEEN SHOWN, kept apart from the answer itself.
 * Somebody who said no has still been asked, and asking again on every page
 * load is the behaviour browsers introduced the permanent block to stop. */
const ASKED = 'eleat.locationAsked';

const listeners = new Set();

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️⚠️ THREE DIFFERENT PLACES, AND THIS FILE USED TO HOLD ONE.
 *
 * Al, 25 September 2026: *"WHERE I REGISTERED ≠ WHERE I AM PHYSICALLY NOW ≠
 * WHERE I AM SEARCHING. Keep those concepts separate."*
 *
 *   WHERE I REGISTERED   `users.market` — the COUNTRY, for billing and tax.
 *                        Nothing in discovery reads it and nothing here does
 *                        either. It is not a location, it is an invoice.
 *   WHERE I AM NOW       the device fix below. Transient, never stored, never
 *                        written to a row, no history of any kind.
 *   WHERE I AM SEARCHING `eleat.centre`. A CHOICE, which the phone may offer
 *                        a default for and must never override.
 *
 * ⚠️ WHAT THE ONE-OBJECT VERSION ACTUALLY DID. Choose Calgary while standing
 * in Vancouver and `followMe()` — correctly — stopped writing. But it was the
 * only thing asking the phone anything, so **Eleat then had no idea where the
 * device was, and never found out again.** Two consequences nobody could see:
 * Directions handed Google *Calgary* as the origin of a walk that starts in
 * Vancouver, and `whatToDoOnOpen()` answered "have" for ever — so somebody who
 * used Eleat in Hong Kong yesterday and opens it in Calgary today was never
 * asked the phone a second time.
 *
 * ⚠️ THE DEVICE FIX IS IN MEMORY AND NOWHERE ELSE. Not localStorage, not a
 * column, not a row. Al: *"Do not build permanent location history."* It lives
 * for as long as the tab does and dies with it — which is also why it is
 * simply absent on a cold open until the phone answers, and every reader here
 * copes with that.
 * ══════════════════════════════════════════════════════════════════════════ */
let device = null;                       // {lat, lng, accuracy_m, at} | null
const deviceListeners = new Set();

export function readDevice() { return device; }

export function setDevice(fix) {
  device = fix;
  deviceListeners.forEach((fn) => { try { fn(fix); } catch { /* not the others' problem */ } });
}

export function useDevice() {
  const [d, setD] = useState(readDevice);
  useEffect(() => {
    deviceListeners.add(setD);
    return () => deviceListeners.delete(setD);
  }, []);
  return d;
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ WHICH MARKET TO ASK THE SERVER FOR — AND `'hong-kong'` WAS TYPED INTO
 * SEVEN SCREENS.
 *
 * Al, 25 September 2026: *"A user registered under HK can later physically be
 * in Calgary and search Calgary."*
 *
 * `/discovery/where` has worked out the right market from a coordinate since
 * migration 047 — anywhere on earth, and honestly null where Eleat has no
 * venues. It is already in the stored centre. Every screen then ignored it and
 * sent the constant, so landing in Calgary drew a Calgary pin above four rails
 * of Hong Kong restaurants.
 *
 * THREE ANSWERS, AND THE THIRD IS THE ONE THAT KEPT BEING MISSED:
 *   a market code  — ask for that market.
 *   FALLBACK       — no centre at all yet. Nothing has been chosen and nothing
 *                    has been found, so the launch market is the opening
 *                    offer. This is the ONLY place Hong Kong may appear.
 *   null           — a real place, correctly named, that Eleat is not in.
 *                    **Not a reason to fall back.** Al: *"do NOT silently show
 *                    Hong Kong restaurants as though they were local."*
 * ══════════════════════════════════════════════════════════════════════════ */
export const FALLBACK_MARKET = 'hong-kong';

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️⚠️ "Validation failed" ON ALL FOUR RAILS — AND IT WAS THIS FUNCTION.
 *
 * Al's own phone in Futian, 25 September 2026: four rails, four red lines
 * reading *"Validation failed"*, and a Try again button.
 *
 * **THE MARKET COMES BACK IN TWO SHAPES AND I ONLY HANDLED ONE.**
 *
 *   GET /discovery/where          →  market: { code, name, timezone, … }
 *   GET /discovery/places/centre  →  market: 'hong-kong'
 *   the city chips on Location    →  market: m.code          (a string)
 *
 * So a centre found by GPS carries an OBJECT. `centre.market || null` handed
 * that object straight to `railQuery`, which interpolated it into the URL as
 * **`[object Object]`**, and the route's own validator refused it. Four
 * requests, four rejections, four red lines.
 *
 * ⚠️ IT IS THE SAME FAULT AS THE PIN AND THE CITY CHIPS AND `/my-place` —
 * a screen reading a shape the server did not send. **Fourth time this week,
 * and this one I introduced myself yesterday** by swapping a hard-coded
 * string for a field without checking what the field actually holds.
 *
 * ⚠️ AND IT WOULD HAVE DONE EXACTLY THIS IN HONG KONG TOO. Nothing about it
 * is Shenzhen-specific; any centre found by the phone rather than chosen from
 * the list broke every rail on the screen.
 * ══════════════════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️⚠️ THE NEAREST CITY ELEAT IS IN — THE OTHER HALF OF NEVER BLANK.
 *
 * `/discovery/where` now answers `nearest` alongside `covered:false`: the
 * closest city Eleat actually operates in, by real distance, and only cities
 * that are switched on. `centreFrom` flattens it onto the stored centre like
 * everything else, so this reads it back.
 *
 * ⚠️ IT IS A SEPARATE FACT FROM `market` AND MUST STAY ONE. `market` is "the
 * city you are standing in, and we are in it". `nearest` is "we are not in
 * your city, but this one is closest". Collapsing them is precisely how a man
 * standing in Futian got a headline about Hong Kong — so every screen that
 * falls back to this has to SAY it is doing so. Knowing where somebody is and
 * having something to sell them there are two different facts.
 * ══════════════════════════════════════════════════════════════════════════ */
export function nearestFor(centre) {
  const n = centre && centre.nearest;
  if (!n) return null;
  if (typeof n === 'object') return n.code ? n : null;
  return { code: String(n), name: null, km: null };
}

/**
 * The market a screen should actually QUERY, and whether it is a fallback.
 *
 * ⚠️ NOT A REPLACEMENT FOR `marketFor` — that still answers the honest
 * question ("is Eleat in this city?") and the headline and the copy still read
 * it. This answers the different question a rail has to ask: "what do I put
 * on the screen so this is not blank?"
 */
export function browseMarket(centre) {
  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️⚠️ NO CENTRE AT ALL IS ITS OWN ANSWER — 30 SEPTEMBER 2026, AND UNTIL
     TODAY IT WAS THE ONE THING AL HAS EXPLICITLY FORBIDDEN.

     `marketFor(null)` returns FALLBACK_MARKET, one screen down, because a
     screen asking "is Eleat in this city?" with nothing to go on has to answer
     something. So the first line of this function got `'hong-kong'` — a
     TRUTHY market — and returned `{ market: 'hong-kong', fallback: null }`.

     **`fallback: null` is what tells every screen there is nothing to
     explain.** So an Eleater who refused the location prompt, anywhere on
     earth, was shown Hong Kong's rails and Hong Kong's headline with not one
     word saying why — which is precisely what Al ruled out on 25 September:
     never SILENTLY show Hong Kong as though it were local. The notice that
     exists for exactly this could not draw, because the code below never ran.

     ⚠️ AND THE COMMENT ON THAT LAST LINE DESCRIBED A BRANCH THAT WAS
     UNREACHABLE. It said "a cold open with location refused" — the one case it
     could never see, because `marketFor` had already answered above it. A
     sentence that is true about what was intended and false about what runs.

     ⚠️ `marketFor` IS DELIBERATELY NOT CHANGED. Explore, View All, Zaryna,
     Preferences and the Location screen all read it, three of them with their
     own `|| FALLBACK_MARKET`, and moving it would change five screens to fix
     one. The question "do we know where this person is?" is asked and answered
     here, once, where the fallback already lives.

     ⚠️ SOMETHING IS STILL SHOWN. `unknown` does not mean an empty screen — the
     rails still run on the launch market, because a blank Home is worse than a
     named guess. It means the screen must SAY it is a guess and offer the
     choice, which is Al's own rule: knowing where somebody is and having
     something to sell them there are two different facts.
     ══════════════════════════════════════════════════════════════════════ */
  if (!centre) return { market: FALLBACK_MARKET, fallback: null, unknown: true };

  const here = marketFor(centre);
  if (here) return { market: here, fallback: null, unknown: false };
  const near = nearestFor(centre);
  if (near) return { market: near.code, fallback: near, unknown: false };
  /* a centre Eleat cannot place at all — it exists, so this is not `unknown`;
     the market is a fallback and the screen says so through `fallback`. */
  return { market: FALLBACK_MARKET, fallback: null, unknown: true };
}

export function marketFor(centre) {
  if (!centre) return FALLBACK_MARKET;
  const m = centre.market;
  if (!m) return null;
  /* the object form from /discovery/where, or the plain code from the rest */
  if (typeof m === 'object') return m.code || null;
  return String(m);
}

/* ══════════════════════════════════════════════════════════════════════════
   ⚠️ WHAT THE PIN SAYS — THE CITY, NEVER THE STREET. Al, 2 October 2026:
   *"we are still all over the place with it… we should be able to click a
   country and city like opentable."*  OpenTable's pin names the metro it is
   showing, not the road the phone is on — so it does not jump every few
   metres, and it always names somewhere Eleat actually has restaurants.
   A place somebody CHOSE (a city, or a place they typed) keeps its own name;
   a GPS fix names its Eleat city, or the nearest one when Eleat is not there
   (which Home's notice already says out loud). Null = Eleat does not know.
   ══════════════════════════════════════════════════════════════════════════ */
export function browsingName(centre) {
  if (!centre) return null;
  if (centre.from !== 'gps' && centre.label) return centre.label;
  const m = centre.market;
  if (m && typeof m === 'object' && (m.name || m.name_en)) return m.name || m.name_en;
  const n = nearestFor(centre);
  if (n && n.name) return n.name;
  return centre.from === 'gps' ? null : (centre.label || null);
}

/* A country's name from its ISO code, in English, from the browser's own
   list — `markets.country` is the code and Eleat keeps no table of names.
   Some engines call HK "Hong Kong SAR China"; the short form is used then. */
export function countryName(code) {
  if (!code) return '';
  /* in the Eleater's own language (i18n.js) — 香港 · 加拿大 · 中国香港 is the
     browser's long Mainland form, so the short one is used there too */
  const loc = { 'zh-HK': 'zh-Hant-HK', 'zh-CN': 'zh-Hans-CN', 'fr-CA': 'fr-CA', 'gu-IN': 'gu-IN' }[uiLang()] || 'en';
  try {
    const long = new Intl.DisplayNames([loc], { type: 'region' }).of(code);
    if (long && !/SAR|特別行政區|特别行政区/.test(long)) return long;
    return new Intl.DisplayNames([loc], { type: 'region', style: 'short' }).of(code) || code;
  } catch { return code; }
}

/** The active discovery market, or null where Eleat does not operate. */
export function useMarket() {
  return marketFor(useCentre());
}

export function readCentre() {
  try {
    const c = JSON.parse(localStorage.getItem(KEY) || 'null');
    return c && typeof c === 'object' ? c : null;
  } catch { return null; }
}

export function writeCentre(next, { keepOnAccount = true } = {}) {
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* storage can be off */ }
  listeners.forEach((fn) => { try { fn(next); } catch { /* one bad listener is not the others' problem */ } });
  /* ⚠️ A CITY THEY TAP IS KEPT ON THEIR ACCOUNT TOO — Al-Noor, 31 Oct 2026:
     signed in with Google on another browser and Zaryna asked "which city"
     again ("it should… remember me"). The choice used to live only in this
     browser. Signed out, it stays in the browser alone. */
  const code = next && next.from === 'chosen' && next.market
    ? (typeof next.market === 'object' ? next.market.code : next.market) : null;
  if (keepOnAccount && code && getToken()) {
    call('/settings/me/city', { method: 'POST', auth: true, body: { market: String(code) } }).catch(() => {});
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   AFTER A SIGN-IN: PUT THEM BACK IN THEIR CITY, AND SAY WELCOME BACK.
   Al-Noor, 31 Oct 2026: "even though I'm registered [it] did not pick me up
   and asked me I'm out of my region. Which city. It should log me into the
   home and say welcome back and remember me."
   The account's Eleat city becomes this browser's chosen city — unless this
   browser already has one chosen (a choice made here, now, wins). Home shows
   "Welcome back, {first name}" once. Never throws: a sign-in never waits on it
   failing. ══════════════════════════════════════════════════════════════════ */
export const WELCOME_KEY = 'eleat.welcomeBack';
export async function restoreHome({ welcome = true } = {}) {
  try {
    /* at most 4 s — a slow server must never hold the sign-in */
    const d = await Promise.race([
      call('/settings/me/home', { auth: true }),
      new Promise((r) => setTimeout(() => r(null), 4000)),
    ]);
    const m = d && d.market;
    const now = readCentre();
    /* a phone standing INSIDE an Eleat city keeps that city — the account's
       city only fills in where Eleat would otherwise have to ask */
    const lost = !now || (() => { const w = browseMarket(now); return !!(w.fallback || w.unknown); })();
    if (m && !(now && now.from === 'chosen') && lost) {
      writeCentre(centreFrom({
        label: m.name_en || m.code, market: m.code,
        lat: Number(m.centre_lat), lng: Number(m.centre_lng), country_code: m.country,
      }, { from: 'chosen' }), { keepOnAccount: false });
    }
    if (welcome) { try { sessionStorage.setItem(WELCOME_KEY, (d && d.first_name) || ''); } catch { /* fine */ } }
  } catch { /* signed in all the same */ }
}

/* ⚠️ EVERY SCREEN LEARNS AT ONCE. Home draws the pin, Explore scopes its
 * search and Zaryna measures distance from the same answer. Without a
 * subscription the pin would keep reading its stale value until a reload —
 * which is exactly the "it still says location" Al reported, one layer up. */
export function useCentre() {
  const [centre, setCentre] = useState(readCentre);
  useEffect(() => {
    listeners.add(setCentre);
    return () => listeners.delete(setCentre);
  }, []);
  return centre;
}

/* ── has the person already answered the browser? ─────────────────────────
 * ⚠️ THIS IS AN OPTIMISATION, NEVER A GATE. Safari on iOS has been observed
 * answering "prompt" for a permission that is actually denied, so a screen
 * that trusted this to decide anything would be wrong on the one platform
 * that matters most here. It is used only to SKIP the explanation for
 * somebody who has already said yes — never to decide whether to ask. */
export async function permissionState() {
  try {
    if (!navigator.permissions || !navigator.permissions.query) return 'unknown';
    const s = await navigator.permissions.query({ name: 'geolocation' });
    return s.state;                       // granted | denied | prompt
  } catch { return 'unknown'; }
}

export function canLocate() {
  return typeof navigator !== 'undefined' && !!navigator.geolocation;
}

/* ── ask the phone, then ask Eleat what that place is called ───────────────
 *
 * ⚠️ `enableHighAccuracy` IS ON AND IT IS NOT A PROMISE OF PRECISION. It asks
 * the device to use GNSS rather than wifi and cell alone. In Hong Kong that
 * moves the answer from roughly a kilometre to roughly ten metres — enough to
 * name the right area, which is all Eleat needs.
 *
 * ⚠️ AND THE COORDINATES ARE KEPT. `/discovery/where` answers with the place
 * and the market and NOT the point it was asked about, so throwing the
 * position away here would leave Zaryna unable to say how far anything is —
 * the exact fault `Location.jsx` records having had.
 */
/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ TWO ROUTES, TWO SHAPES — AND THE PIN READ THE ONE THAT WAS NESTED.
 *
 * Al, 24 September 2026: *"it asked me for my location. I put everything in
 * and at the end, it still says set location."*  He did nothing wrong and the
 * server did nothing wrong.
 *
 *   GET /discovery/where          →  { place: { label, … }, market: {…}, … }
 *   GET /discovery/places/centre  →  { label, market, lat, lng, … }
 *
 * One is nested, one is flat, and both were spread into the same stored
 * object. So the GPS path wrote `{ place: { label } }` and every screen reads
 * `centre.label` — `undefined`, every time, on three separate code paths.
 *
 * ⚠️ AND IT LOOKED ALIVE, WHICH IS WHY IT SURVIVED. `lat` and `lng` ARE
 * written correctly, and lat/lng is all the rails, the distances and "near
 * you" read. Everything worked except the one field a person can actually
 * see. Nothing threw and nothing logged.
 *
 * ⚠️ SO THE UNWRAPPING LIVES HERE, ONCE. `Location.jsx` already carried a
 * hand-written `label: c.label || label` patch on the one path that did not
 * need it — the fix for this bug was sitting in the file, applied to the
 * wrong line. Three copies of a shape rule is how this happened.
 * ══════════════════════════════════════════════════════════════════════════ */
/* the city an Eleater is in, to keep on their account (11 Oct 2026): the
   Eleat city the phone's position falls in, else the place's own name (a
   city Eleat is not in yet). Null when the phone has said nothing. */
export function whereCity(centre = readCentre()) {
  if (!centre) return null;
  const m = centre.market;
  const code = m && typeof m === 'object' ? m.code : m;
  if (code) return String(code);
  return centre.locality ? String(centre.locality).slice(0, 80) : null;
}

export function centreFrom(answer, extra = {}) {
  const d = answer || {};
  /* the nested `place` is flattened UP; anything already flat wins, because
     the flat route's own fields are the same names and are already correct */
  return { ...(d.place || {}), ...d, ...extra };
}

/* ══════════════════════════════════════════════════════════════════════════
 * ONE ASK OF THE PHONE, TWO THINGS IT ANSWERS.
 *
 * `moveCentre` is the whole difference between the two callers:
 *
 *   findMe()        — somebody PRESSED "Use my location". They are asking to
 *                     look where they are, so the centre moves.
 *   refreshDevice() — the app opened and wants to know where the phone is.
 *                     It must NOT move a centre the Eleater chose.
 *
 * ⚠️ AND IT STILL FILLS AN EMPTY CENTRE. Somebody with no centre at all is not
 * being overridden, they are being offered a first one — which is the "GPS
 * provides a default, GPS is not a prison" rule, in the one place it belongs.
 * A centre already marked `from: 'gps'` is Eleat's own fix and follows too.
 * ══════════════════════════════════════════════════════════════════════════ */
function mayMoveCentre() {
  const now = readCentre();
  return !now || now.from === 'gps';
}

export function findMe({ timeout = 12000, moveCentre = true } = {}) {
  return new Promise((resolve, reject) => {
    if (!canLocate()) { reject(new Error('This device cannot share its position.')); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        /* ⚠️ THE DEVICE FIX IS RECORDED FIRST AND UNCONDITIONALLY. It is true
           whatever the Eleater has chosen to browse, and Directions needs it
           to exist even when the centre is a city on the other side of the
           world. It is also recorded BEFORE the network call, so a geocoder
           that times out does not cost us knowing where the phone is. */
        setDevice({
          lat, lng,
          accuracy_m: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
          at: Date.now(),
        });
        /* ⚠️⚠️ THE ACCURACY GOES UP WITH THE POINT — iOS 14 / ANDROID 12.
           Both let somebody grant a deliberately fuzzy position (Apple
           documents 1–20 km), and the server cannot tell a precise fix from a
           fuzzy one by looking at the coordinate. Without this the pin names a
           street from a point that may be twenty kilometres away, confidently,
           with nothing thrown and nothing logged. `coarsen()` in
           where.service.js decides how much of the name comes back. */
        /* ⚠️ `0` MEANS "THE PHONE DID NOT SAY", and the server reads it as
           precise — which is the behaviour that shipped before any of this, so
           a desktop or an older browser is unaffected. It is sent as a real
           number rather than an empty string because a query Eleat builds by
           sticking a maybe-empty fragment onto a URL is a query no gate can
           read, and this line had exactly that fault for about four minutes. */
        const accM = Number.isFinite(pos.coords.accuracy)
          ? Math.round(pos.coords.accuracy) : 0;
        call(`/discovery/where?lat=${lat}&lng=${lng}&acc=${accM}`)
          .then((d) => {
            const next = centreFrom(d, { lat, lng, from: 'gps' });
            /* ⚠️ A DELIBERATELY CHOSEN PLACE SURVIVES THIS. Al: *"do not stop
               knowing where the physical device is simply because I selected
               another discovery location"* — and equally, do not un-choose
               their choice just because we asked the phone a question. */
            if (moveCentre || mayMoveCentre()) writeCentre(next);
            resolve(next);
          })
          .catch(reject);
      },
      (e) => {
        /* ⚠️ REFUSED IS NOT BROKEN, AND IT IS RECORDED AS ANSWERED.
         * Somebody who says no has answered the question; the sheet does not
         * come back. They pick a place instead, which is the other half of
         * the pattern Uber and OpenTable both ship. */
        try { localStorage.setItem(ASKED, e.code === 1 ? 'denied' : 'failed'); } catch { /* fine */ }
        reject(new Error(e.code === 1
          ? 'Eleat could not use your location. You can choose a place instead.'
          : 'Eleat could not get your position. You can choose a place instead.'));
      },
      { enableHighAccuracy: true, timeout, maximumAge: 60000 }
    );
  });
}

/** Where is the phone, right now — without touching what they are browsing. */
export function refreshDevice(opts = {}) {
  return findMe({ ...opts, moveCentre: false });
}

/* ── what should happen the moment the app opens ───────────────────────────
 *
 * Four answers, and only one of them puts anything on the screen:
 *
 *   'have'   — a centre is already stored. Say nothing.
 *   'silent' — permission is already granted. Find them, show no sheet; the
 *              phone will not prompt, so an explanation would be noise.
 *   'ask'    — never asked. Show one line of why, then the browser's prompt.
 *   'choose' — asked and refused, or the device cannot. Never prompt again;
 *              the way forward is picking a place.
 *
 * ⚠️ THE EXPLANATION COMES BEFORE THE PROMPT, and that ordering is the whole
 * reason the accept rate is any good. web.dev: ask in context, say what it is
 * for, and never fire a permission prompt at somebody who has no idea why.
 */
/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ `if (readCentre()) return 'have'` WAS THE FIRST LINE, AND IT ANSWERED
 * THE WRONG QUESTION.
 *
 * Al, 25 September 2026: *"The existence of a stored discovery centre must NOT
 * mean 'we already know where the device currently is'. Those are different
 * questions… If I used Eleat yesterday in Hong Kong and open it today in
 * Calgary, the application must be capable of obtaining my current Calgary
 * device location."*
 *
 * Having somewhere to LOOK was being read as knowing where they ARE, so the
 * one person most likely to have moved — somebody who has used Eleat before —
 * was the one person the phone was never asked about again.
 *
 * ⚠️ PERMISSION IS NOW ASKED FIRST, and the centre only decides the case where
 * the browser will not tell us anything useful. Nothing here prompts anybody
 * twice: 'silent' means permission is ALREADY granted, so no prompt appears.
 * ══════════════════════════════════════════════════════════════════════════ */
export async function whatToDoOnOpen() {
  if (!canLocate()) return 'choose';
  let asked = null;
  try { asked = localStorage.getItem(ASKED); } catch { /* fine */ }
  const state = await permissionState();
  /* already said yes — find them silently, every open, however old the centre */
  if (state === 'granted') return 'silent';
  if (state === 'denied') return 'choose';
  if (asked === 'denied' || asked === 'failed') return 'choose';
  /* ⚠️ SOMEBODY WHO PICKED A PLACE IS NOT ASKED AGAIN. `markAsked('chose')` is
     written by the sheet's other button, so they land here and keep what they
     chose — the sheet does not come back at them on every open. */
  if (readCentre()) return 'have';
  return 'ask';
}

export function markAsked(how) {
  try { localStorage.setItem(ASKED, how); } catch { /* fine */ }
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ IT FOLLOWS YOU. THIS IS THE WHOLE POINT AND IT WAS MISSING.
 *
 * Al, 24 September 2026: *"if I walk 100 meters left, right, up or down…
 * recheck my location, see where I am, and find the closest restaurants within
 * a 500 meter radius… So every time I walk, it's going to change."*
 *
 * The first version asked once and remembered. Open in TST, ride to Central,
 * and the app still said TST. That is not a location feature, it is a
 * location snapshot.
 *
 * ── THE ONE RULE THAT STOPS IT BEING ANNOYING ──────────────────────────────
 *
 * ⚠️ IT ONLY FOLLOWS A POSITION IT FOUND ITSELF. If the Eleater deliberately
 * set the app to Kennedy Town while sitting in Wan Chai, walking to the next
 * street must not drag them back. `from: 'gps'` is the flag: Eleat's own fix
 * follows the phone, a chosen place stays chosen until they change it or tap
 * "use my location" again.
 *
 * ⚠️ AND IT STOPS WHEN THE SCREEN IS NOT BEING LOOKED AT. A geolocation watch
 * left running is a battery complaint with Eleat's name on it.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ 100 METRES WAS MINE. I LABELLED IT "AL'S OWN NUMBER" AND IT WAS NOT.
 *
 * Al, 25 September 2026: *"as a person fucking moves, everything moves on him.
 * He can, his geolocation pin will move every single fucking step… Not 110
 * fucking meters or 112 fucking meters. Within five meters, it picks you up.
 * And then as you're walking along, fucking Google Maps."*
 *
 * He had said it before. The comment that used to sit here claimed 100 m was
 * his, and then justified it with a noise-floor argument I made up. **Neither
 * half was true.** The number he keeps giving me is FIVE.
 *
 * ⚠️ THE NOISE FLOOR IS STILL REAL, AND IT IS NOT A REASON TO IGNORE HIM. A
 * phone standing still jitters, so re-resolving on every jitter would burn the
 * radio and the geocoder for no movement. The answer is not a bigger number —
 * it is to use the fix's OWN accuracy: move when the step is bigger than the
 * error the phone itself reports, floored at Al's five metres. A phone with a
 * 4 m fix follows a five-metre step. A phone with a 40 m fix waits for 40,
 * because below that it does not know it moved.
 * ══════════════════════════════════════════════════════════════════════════ */
const MOVED_M = 5;

/** Metres between two points. Equirectangular — exact enough far below the
 *  error in the fix itself, and it does not need a trigonometry library. */
export function metresBetween(a, b) {
  if (!a || !b || !Number.isFinite(a.lat) || !Number.isFinite(b.lat)) return Infinity;
  const R = 6371000;
  const rad = Math.PI / 180;
  const x = (b.lng - a.lng) * rad * Math.cos(((a.lat + b.lat) / 2) * rad);
  const y = (b.lat - a.lat) * rad;
  return Math.sqrt(x * x + y * y) * R;
}

let watchId = null;

export function followMe() {
  if (watchId !== null || !canLocate()) return () => {};

  const moved = (pos) => {
    const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };

    /* ⚠️ THE PHONE'S OWN ERROR DECIDES, NOT A NUMBER I PICKED. `accuracy` is
       the radius in metres the phone says it is confident to. A step smaller
       than that is not a step, it is jitter — and a step bigger than it is
       real movement, at five metres or five hundred. */
    const errorM = Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : MOVED_M;
    const step = Math.max(MOVED_M, errorM);

    /* ══════════════════════════════════════════════════════════════════
       ⚠️⚠️ WHERE THE PHONE IS IS RECORDED EVERY TIME, WHATEVER IS BEING
       BROWSED — AND THIS RETURN USED TO SIT ABOVE IT.

       The `from !== 'gps'` guard is correct and Al asked for it to be kept:
       choose Calgary in Vancouver and walking to the next street must not
       drag you home. But it was bailing out of the WHOLE handler, so the
       moment anybody chose a place Eleat stopped tracking the device at
       all — and Directions, which starts from the phone, had nothing.

       Two lines, in this order: the fix is always true, the centre is a
       choice. The guard below now protects only the choice.
       ══════════════════════════════════════════════════════════════════ */
    const lastFix = readDevice();
    if (!lastFix || metresBetween(lastFix, here) >= step) {
      setDevice({ ...here, accuracy_m: errorM, at: Date.now() });
    }

    /* ⚠️ NAMED `centreNow`, NOT `now`. It was `now`, two lines under a
       `Date.now()`, and `verify_no_tdz.js` refused the file for it — reading
       the property access as a use of the variable before its declaration.
       The gate was wrong about the mechanism and right about the code: a
       local called `now` beside `Date.now()` is a trap for whoever reads it
       next, and weakening a gate to accommodate my own naming is how the
       two-plus-two check came to reward the wrong behaviour. */
    const centreNow = readCentre();
    /* ⚠️ A CHOSEN PLACE IS NOT OVERRIDDEN — see the rule above. */
    if (centreNow && centreNow.from !== 'gps') return;

    if (centreNow && metresBetween(centreNow, here) < step) return;

    /* ⚠️ THE EXACT POINT GOES UP. It used to be rounded before anything saw
       it, which is how the pin ended up naming a street up to 110 m away. */
    /* ⚠️ THE WATCH SENDS IT TOO. Precise Location can be switched off in
       Settings while Eleat is open, so the very next fix from this same watch
       can be a fuzzy one — sending it on the first ask only would name streets
       off a kilometre-wide point for the rest of the session. */
    call(`/discovery/where?lat=${here.lat}&lng=${here.lng}&acc=${Math.round(errorM)}`)
      .then((d) => writeCentre(centreFrom(d, {
        ...here, accuracy_m: errorM, from: 'gps',
      })))
      .catch(() => { /* a wobble must never clear where they are */ });
  };

  const start = () => {
    if (watchId !== null) return;
    watchId = navigator.geolocation.watchPosition(moved, () => {}, {
      enableHighAccuracy: true,
      /* ⚠️ `maximumAge: 0` — NEVER A CACHED FIX. It was 30 seconds, on the
         reasoning that somebody walking 100 m took longer than that. At five
         metres that reasoning is gone: a cached fix is a stale pin, and Al's
         whole point is that it moves with him. */
      maximumAge: 0,
      timeout: 20000,
    });
  };
  const stop = () => {
    if (watchId === null) return;
    navigator.geolocation.clearWatch(watchId);
    watchId = null;
  };

  const onVisibility = () => (document.hidden ? stop() : start());
  document.addEventListener('visibilitychange', onVisibility);
  if (!document.hidden) start();

  return () => { document.removeEventListener('visibilitychange', onVisibility); stop(); };
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ HOW FAR "NEAR ME" REACHES — IT STARTS TIGHT AND WIDENS.
 *
 * Al: *"find the closest restaurants within a 500 meter radius and then goes
 * out."*  So the first ask is 500 m — walking distance, which is what "near
 * me" means to somebody standing on a street. If that comes back thin, the
 * next ask is wider, and the last one drops the radius entirely and searches
 * the whole market.
 *
 * ⚠️ THE LAST RUNG IS NOT "NO ANSWER". A market Eleat has just opened may have
 * six venues in it, none within 500 m of anybody. Showing an empty rail there
 * would read as Eleat being broken rather than Eleat being new.
 * ══════════════════════════════════════════════════════════════════════════ */
export const RADIUS_LADDER = [0.5, 1, 2, 5, null];   // km; null = the whole market

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ THE DISTANCE PREFERENCE EXISTED, SAVED CORRECTLY, AND WAS READ BY
 * NOTHING.
 *
 * `Preferences.jsx` offers 1 · 8 · 25 km, writes `max_distance_km` onto the
 * profile through `PUT /settings/me`, and loads it back into its own screen.
 * **No discovery query anywhere read it.** Home walked its own ladder and
 * Explore sent no radius at all, so the control saved a number and changed
 * nothing an Eleater could see — and the Location screen said so out loud:
 * *"Distance filtering is not switched on yet."*
 *
 * ⚠️ IT IS MEASURED FROM THE ACTIVE DISCOVERY CENTRE, NEVER THE PHONE.
 * Al: *"It must NOT measure 8 km from Vancouver merely because the phone is
 * physically there."*  Both callers pass the centre's point, which is the
 * device's own point whenever Current Location is the active mode — so one
 * rule covers both cases and there is no second definition of "from here".
 *
 * ⚠️ AND IT IS MIRRORED INTO THE BROWSER BECAUSE HOME IS DRAWN BEFORE ANY
 * PROFILE ARRIVES. `whoAmI()` needs a token and a round trip; the four rails
 * fire on the first paint. Reading a stale copy and correcting it when the
 * profile lands beats drawing every rail twice. The profile row stays the
 * source of truth — this is a cache, and `rememberMaxKm` is the only writer.
 * ══════════════════════════════════════════════════════════════════════════ */
const MAX_KM = 'eleat.maxKm';

export function readMaxKm() {
  try {
    const v = Number(localStorage.getItem(MAX_KM));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch { return null; }
}

export function rememberMaxKm(km) {
  try {
    if (km == null) localStorage.removeItem(MAX_KM);
    else localStorage.setItem(MAX_KM, String(km));
  } catch { /* storage can be off; the profile still has it */ }
}

/**
 * The rungs Home walks, capped by whatever the Eleater asked for.
 *
 * ⚠️ A PREFERENCE REMOVES THE WHOLE-MARKET RUNG, AND THAT IS THE POINT. The
 * ladder's last step is "search the entire market" so a new city never shows
 * an empty rail — reasonable as a default, and a broken promise once somebody
 * has said 8 km. Nothing beyond their number is offered; a thin rail is the
 * honest answer to a limit they set themselves.
 */
export function radiusLadder(maxKm = readMaxKm()) {
  if (!maxKm) return RADIUS_LADDER;
  const rungs = RADIUS_LADDER.filter((km) => km != null && km < maxKm);
  return [...rungs, maxKm];
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️⚠️ BROWSING SOMEWHERE YOU ARE NOT — AND NOTHING EVER SAID SO.
 *
 * Al, 27 September 2026, standing in Futian: *"look at the fucking location on
 * the home page it says fucking hong kong and i am in futian shenzhen, what the
 * fuck is that. and i still dont think you have that sorted out correctly for
 * geo location and i keep telling you this and you never address it."*
 *
 * ⚠️ THE SERVER WAS RIGHT AND THE PHONE IGNORED IT. Proved live on `eleat-api`
 * before a word of diagnosis (RULE 1) — `GET /discovery/where` for his exact
 * point answers `{"place":{"label":"Yi Tian Lu, 中心城","locality":"Shenzhen",
 * "country_code":"CN"},"market":null,"covered":false,"nearest":{"Hong Kong",
 * 27.2km}}`. Eleat knew the street. **His phone had `Hong Kong` stored with
 * `from:'chosen'`, and a chosen place outranks GPS.**
 *
 * ⚠️ AND THE GUARD IS NOT THE BUG — AL ASKED FOR IT AND IT STAYS. Choose
 * Calgary in Vancouver and walking to the next street must not drag you home.
 * **The bug is that the choice is PERMANENT AND INVISIBLE.** He tapped Hong
 * Kong in the city list once — almost certainly on 24 September, testing the
 * chip that had just been fixed — and every screen has shown Hong Kong ever
 * since with nothing saying why and nothing offering a way back.
 *
 * ⚠️ EVERYTHING THIS NEEDS WAS ALREADY BEING RECORDED AND READ BY NOTHING.
 * `setDevice()` writes the phone's real fix on every update **even while a
 * chosen centre is in force** — that was fixed on 25 September so Directions
 * could start from where somebody is standing. So Eleat has held both facts,
 * separately and correctly, and has never once compared them.
 *
 * Returns null unless they genuinely differ, so a person browsing where they
 * actually are never sees a word of this.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ 25 km, AND THE NUMBER IS NOT ARBITRARY. Hong Kong's own coverage radius
 * is 45 km, so a smaller threshold would nag somebody in Sai Kung who chose
 * Hong Kong on purpose. Futian is 27.2 km from the centre point — outside
 * this, in another country, which is exactly the case that must be caught. */
const ELSEWHERE_M = 25000;

export function browsingElsewhere() {
  const centre = readCentre();
  const fix = readDevice();
  /* no choice in force, or the phone has never reported — nothing to say */
  if (!centre || centre.from !== 'chosen') return null;
  if (!fix || !Number.isFinite(fix.lat)) return null;
  if (!Number.isFinite(centre.lat)) return null;
  const away = metresBetween(centre, fix);
  if (!(away >= ELSEWHERE_M)) return null;
  return { chosen: centre.label || null, km: Math.round(away / 1000), fix };
}

/* ⚠️ AND THE WAY BACK IS THE CONTROL THAT ALREADY EXISTS. `findMe()` is what
 * the Location screen's own button calls — one definition, so the tap on Home
 * and the tap on that screen cannot come to mean two different things. */
export function useWhereIAm(opts = {}) { return findMe(opts); }

/* ══════════════════════════════════════════════════════════════════════════
 * ✅ A CHOSEN CITY IS REMEMBERED — AL, 2 OCTOBER 2026 (replaces his 27 Sep
 * rule that it reset on every open). Asked "do you want Eleat to remember it,
 * as OpenTable does?" he said yes, and asked what happens when he travels.
 * THE TRAVEL ANSWER IS ALREADY HERE, and it is the big apps' pattern (Uber
 * Eats / DoorDash "looks like you're somewhere new", OpenTable "Not correct?
 * Get current location"): the choice is kept, the phone is still read on
 * every open (`refreshDevice`, which never overwrites a choice), and when the
 * phone is 25 km+ from the chosen city Home says so — `browsingElsewhere()`
 * above: "Browsing Hong Kong — you're about 8,800 km away · Use where I am".
 * One tap and Eleat is where they are; nothing switches behind their back.
 * `forgetChosenCentreOnOpen()` and its boot call are deleted, not kept beside
 * this (RULE 3).
 * ══════════════════════════════════════════════════════════════════════════ */
